"use client";

import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@patient-watch/ui/chart";
import { cn } from "@patient-watch/ui";
import { CartesianGrid, ComposedChart, Line, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { CHART_CONSTANTS } from "../chartConstants";

export interface BenefitRiskDataPoint {
    timepoint: string;
    meanAtrs: number;
    complicationRate: number;
    meanEq5d?: number;
}

export interface BenefitRiskChartProps {
    data: BenefitRiskDataPoint[];
    outcomeLabel?: string;
    riskLabel?: string;
    outcomeAxisLabel?: string;
    riskAxisLabel?: string;
    className?: string;
    height?: number;
}

const BENEFIT_RISK_CHART_CONFIG = {
    meanAtrs: {
        label: "Mean ATRS",
        color: "hsl(var(--chart-1))",
    },
    complicationRate: {
        label: "Serious event rate",
        color: "hsl(var(--chart-2))",
    },
} satisfies ChartConfig;

export function BenefitRiskChart({
    data,
    outcomeLabel = "Mean ATRS",
    riskLabel = "Serious event rate",
    outcomeAxisLabel = "ATRS",
    riskAxisLabel = "Event %",
    className,
    height = 320,
}: BenefitRiskChartProps) {
    const chartConfig: ChartConfig = {
        meanAtrs: { ...BENEFIT_RISK_CHART_CONFIG.meanAtrs, label: outcomeLabel },
        complicationRate: { ...BENEFIT_RISK_CHART_CONFIG.complicationRate, label: riskLabel },
    };

    return (
        <div className={cn("flex flex-col gap-3", className)}>
            <div className="flex flex-wrap gap-4 text-[11px] text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                    <span className="rounded-full bg-[hsl(var(--chart-1))] w-3 h-0.5" aria-hidden />
                    {outcomeLabel}
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span
                        className="rounded-full border border-dashed border-[hsl(var(--chart-2))] bg-[hsl(var(--chart-2))]/30 w-3 h-0.5"
                        aria-hidden
                    />
                    {riskLabel}
                </span>
            </div>
            <ChartContainer
                config={chartConfig}
                className="bg-slate-50/50 p-2 border border-slate-100 rounded-lg w-full"
            >
                <ResponsiveContainer width="100%" height={height}>
                    <ComposedChart data={data} margin={{ ...CHART_CONSTANTS.MARGIN, top: 12, right: 8, left: 4 }}>
                        <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                            dataKey="timepoint"
                            tickLine={false}
                            axisLine={false}
                            tickMargin={CHART_CONSTANTS.AXIS_TICK_MARGIN}
                            tick={{ fontSize: CHART_CONSTANTS.AXIS_FONT_SIZE }}
                        />
                        <YAxis
                            yAxisId="benefit"
                            domain={[0, 100]}
                            ticks={[0, 25, 50, 75, 100]}
                            tickLine={false}
                            axisLine={false}
                            tickMargin={CHART_CONSTANTS.Y_AXIS_TICK_MARGIN}
                            tick={{ fontSize: CHART_CONSTANTS.AXIS_FONT_SIZE }}
                            width={CHART_CONSTANTS.Y_AXIS_WIDTH}
                            label={{
                                value: outcomeAxisLabel,
                                angle: -90,
                                position: "insideLeft",
                                style: { fontSize: 10, fill: "hsl(var(--chart-1))" },
                            }}
                        />
                        <YAxis
                            yAxisId="risk"
                            orientation="right"
                            domain={[0, 10]}
                            ticks={[0, 2, 4, 6, 8, 10]}
                            tickLine={false}
                            axisLine={false}
                            tickMargin={CHART_CONSTANTS.Y_AXIS_TICK_MARGIN}
                            tick={{ fontSize: CHART_CONSTANTS.AXIS_FONT_SIZE }}
                            width={CHART_CONSTANTS.Y_AXIS_WIDTH + 4}
                            label={{
                                value: riskAxisLabel,
                                angle: 90,
                                position: "insideRight",
                                style: { fontSize: 10, fill: "hsl(var(--chart-2))" },
                            }}
                        />
                        <ChartTooltip
                            content={
                                <ChartTooltipContent
                                    indicator="line"
                                    formatter={(value, name) => {
                                        if (name === "meanAtrs") {
                                            return (
                                                <>
                                                    <span>{outcomeLabel}</span>
                                                    <span className="font-mono font-medium tabular-nums text-slate-950">
                                                        {value}
                                                    </span>
                                                </>
                                            );
                                        }
                                        if (name === "complicationRate") {
                                            return (
                                                <>
                                                    <span>{riskLabel}</span>
                                                    <span className="font-mono font-medium tabular-nums text-slate-950">
                                                        {value}%
                                                    </span>
                                                </>
                                            );
                                        }
                                        return null;
                                    }}
                                    labelFormatter={(label) => String(label)}
                                />
                            }
                        />
                        <Line
                            yAxisId="benefit"
                            type="monotone"
                            dataKey="meanAtrs"
                            stroke="var(--color-meanAtrs)"
                            strokeWidth={3}
                            dot={{ r: 4, fill: "var(--color-meanAtrs)" }}
                            activeDot={{ r: 6 }}
                        />
                        <Line
                            yAxisId="risk"
                            type="monotone"
                            dataKey="complicationRate"
                            stroke="var(--color-complicationRate)"
                            strokeWidth={2.5}
                            strokeDasharray="5 4"
                            dot={{ r: 2.5, fill: "var(--color-complicationRate)" }}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </ChartContainer>
        </div>
    );
}
